import { useState } from "react";
import { toast } from "sonner";
import { Trash2 } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

// Trash icon + confirmation dialog. Hard-deletes the row from the database
// (direct booking or OTA event) so the calendar cell is freed immediately.
// Distinct from soft-cancel which keeps the audit trail.
export default function DeleteBookingButton({ booking, onDeleted, IT }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const isOta = !!booking?.is_ota;
  const confirmDelete = async () => {
    setBusy(true);
    try {
      await api.delete(`/bookings/${booking.id}/hard`);
      toast.success(IT ? "Prenotazione eliminata definitivamente" : "Booking permanently deleted");
      setOpen(false);
      onDeleted?.();
    } catch (e) {
      toast.error(errMsg(e, IT ? "Errore eliminazione" : "Delete error"));
    } finally { setBusy(false); }
  };
  return (
    <>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setOpen(true); }}
        data-testid={`delete-booking-btn-${booking.id}`}
        className="inline-flex items-center gap-1.5 text-[11px] px-2.5 py-1 rounded-full border border-destructive/40 bg-destructive/5 text-destructive hover:bg-destructive hover:text-white transition-all lift ml-1 whitespace-nowrap"
        title={IT ? "Elimina definitivamente dal database" : "Permanently delete from database"}
      >
        <Trash2 className="w-3 h-3"/>
        {IT ? "Cancella" : "Delete"}
      </button>
      <Dialog open={open} onOpenChange={(o) => !busy && setOpen(o)}>
        <DialogContent className="max-w-md" data-testid="delete-booking-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl flex items-center gap-2">
              <Trash2 className="w-5 h-5 text-destructive"/>
              {IT ? "Cancellazione definitiva" : "Permanent deletion"}
            </DialogTitle>
            <DialogDescription>
              {IT
                ? "Questa azione è irreversibile. La prenotazione verrà rimossa completamente dal database e la data sarà immediatamente disponibile per nuove prenotazioni."
                : "This action is irreversible. The booking will be fully removed from the database and the date will be immediately available for new reservations."}
            </DialogDescription>
          </DialogHeader>
          <div className="text-sm border border-border rounded-md p-3 bg-sand/40 space-y-1">
            <div><span className="text-muted-foreground">{IT ? "Ospite" : "Guest"}:</span> <b>{booking.guest_name || "—"}</b></div>
            <div><span className="text-muted-foreground">{IT ? "Camera" : "Room"}:</span> {booking.room_name || "—"}</div>
            <div><span className="text-muted-foreground">Check-in:</span> {booking.check_in} → {booking.check_out}</div>
            {isOta && (
              <div className="text-[11px] text-muted-foreground italic pt-1 border-t border-border mt-1">
                {IT ? "Nota: prenotazione OTA. Se il feed iCal viene risincronizzato dal canale sorgente, la riga potrebbe ricomparire." : "Note: OTA booking. If the iCal feed re-syncs from the source channel, the row may reappear."}
              </div>
            )}
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={() => setOpen(false)}
              disabled={busy}
              className="text-sm px-4 py-2 rounded-md border border-border text-muted-foreground hover:border-primary hover:text-primary transition-all"
              data-testid="delete-booking-cancel-btn"
            >
              {IT ? "Annulla" : "Cancel"}
            </button>
            <button
              type="button"
              onClick={confirmDelete}
              disabled={busy}
              className="text-sm px-4 py-2 rounded-md bg-destructive text-white hover:bg-destructive/90 transition-all disabled:opacity-50 font-semibold"
              data-testid="delete-booking-confirm-btn"
            >
              <Trash2 className="w-3.5 h-3.5 inline mr-1"/>
              {busy ? (IT ? "Eliminazione…" : "Deleting…") : (IT ? "Conferma cancellazione" : "Confirm deletion")}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
